import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, withKeys as _withKeys } from "vue";
const _hoisted_1 = {
  class: "flex items-center justify-between w-full"
};
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useChargesStore } from '@/services/UserService/useChargesStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMatterChangeDetection } from '@/composables/useMatterChangeDetection';
import EventTracker from '@/services/eventTracker';
import { TrackingCategory, TrackingAction } from '@/constants/trackingConstants';
import CreateChargeForm from './CreateChargeForm.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import DeleteRecurringFeesDialog from './DeleteRecurringFeesDialog.vue';
import RouteService from '@/services/RouteService';
export default {
  __name: 'CreateChargeModal',
  props: {
    state: Object,
    stateParams: Object,
    rootScope: Object,
    tracking: {
      type: Object,
      required: false
    },
    entryPoint: {
      type: String,
      default: 'matter_page'
    },
    modelValue: {
      type: Boolean,
      default: undefined
    },
    isfromVue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'fixedFeeCreated', 'update:modelValue'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const emit = __emit;
    const props = __props;
    const chargesStore = useChargesStore();
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    const routeService = new RouteService(props);

    // Derive effective entryPoint: prefer stateParams.entryPoint (route-based), fall back to prop
    const effectiveEntryPoint = computed(() => props.stateParams?.entryPoint || props.entryPoint);
    const {
      hasMatterChanged,
      handleMatterChange,
      getMatterChangeMessage,
      calculateAmountChange
    } = useMatterChangeDetection();
    const dialogOpen = ref(false);

    // Watch modelValue and sync with dialogOpen
    watch(() => props.modelValue, newValue => {
      if (newValue !== undefined) {
        dialogOpen.value = newValue;
      }
    }, {
      immediate: true
    });

    // Watch dialogOpen and emit changes when controlled
    watch(dialogOpen, newValue => {
      if (props.modelValue !== undefined) {
        emit('update:modelValue', newValue);
      }
    });
    const cardRef = ref(null);
    const formRef = ref(null);
    const matterChangeConfirmationRef = ref(null);
    const deleteConfirmationRef = ref(null);
    const deleteRecurringRef = ref(null);
    const state = reactive({
      chargeId: null,
      originalMatterId: null,
      originalClientId: null,
      isBilled: false,
      isSaving: false,
      isDeleting: false
    });
    const chargeTitle = computed(() => {
      const suffix = fixedFeeTemplate ? 'Fixed Fee Template' : 'Fixed Fee';
      return state.chargeId ? `Edit ${suffix}` : `Create ${suffix}`;
    });
    const matterChangeMessage = computed(() => getMatterChangeMessage());
    const {
      fixedFeeTemplate
    } = props.stateParams;
    const handleChargeLoaded = chargeData => {
      state.chargeId = chargeData.id || null;
      state.isBilled = chargeData.billed || false;
      if (chargeData.id && state.originalMatterId === null) {
        state.originalMatterId = chargeData.matter?.id || chargeData.cm?.matter?.id || null;
        state.originalClientId = chargeData.matter?.client?.id || chargeData.cm?.matter?.client?.id || chargeData.matterDetail?.client?.id || null;
      }
    };
    const handleFixedFeeSaved = () => {
      if (props.rootScope?.$broadcast) {
        props.rootScope.$broadcast('fixedFeeUpdated');
      }
    };

    /**
     * Shared logic for updating charge with matter change detection
     */
    const updateChargeWithMatterChange = async (chargeId, chargeData, originalAmount) => {
      const charge = formRef.value.getChargeData();
      const newFixedFeeAmount = calculateAmountChange(charge.amount, originalAmount);
      const newMatterId = chargeData.matter?.id || charge.cm?.matter?.id;
      await chargesStore.updateCharge(chargeId, chargeData, newFixedFeeAmount);
      if (hasMatterChanged(state.originalMatterId, newMatterId)) {
        const {
          newOriginalMatterId
        } = await handleMatterChange({
          chargeId,
          originalMatterId: state.originalMatterId,
          newMatterId,
          showConfirmation: () => matterChangeConfirmationRef.value.open()
        });
        if (newOriginalMatterId) {
          state.originalMatterId = newOriginalMatterId;
        }
      }
    };

    /**
     * Handle save and continue (from unsaved changes dialog)
     */
    const handleSaveAndContinue = async ({
      chargeData,
      chargeId,
      originalAmount
    }) => {
      try {
        if (chargeId) {
          await updateChargeWithMatterChange(chargeId, chargeData, originalAmount);
        } else {
          await chargesStore.createChargeWithType(chargeData, fixedFeeTemplate);
        }
        return true;
      } catch (error) {
        handleError(error);
        return false;
      }
    };
    const submitCharge = async chargeData => {
      state.isSaving = true;
      let fixedFee = {};
      try {
        if (state.chargeId) {
          await updateCharge(state.chargeId, chargeData);
        } else {
          fixedFee = await createCharge(chargeData);

          // Track Fixed Fee Created event
          if (props.tracking) {
            EventTracker.trackEvent(props.tracking, TrackingCategory.FIXED_FEE, TrackingAction.CREATED, {
              entryPoint: effectiveEntryPoint.value,
              isTemplate: !!fixedFeeTemplate
            });
          }
        }
        handleSuccess(`${fixedFeeTemplate ? 'Fixed fee template' : 'Fixed fee'} ${state.chargeId ? 'updated' : 'created'} successfully`);
        cancel(fixedFee);
      } catch (error) {
        handleError(error);
      } finally {
        state.isSaving = false;
      }
    };
    const saveCharge = async () => {
      if (!formRef.value) return;
      const allocationValid = formRef.value.getAllocationValid();
      if (!fixedFeeTemplate && !allocationValid) {
        handleError('Allocation amounts must total the fixed fee amount');
        return;
      }
      const validationErrors = formRef.value.validateForm();
      if (validationErrors.length) {
        validationErrors.forEach(error => handleError(error));
        return;
      }
      const chargeData = formRef.value.prepareData();
      if (!chargeData) {
        return;
      }
      await submitCharge(chargeData);
    };
    const deleteCharge = async () => {
      if (!formRef.value) return;
      try {
        const charge = formRef.value.getChargeData();

        // If this is a child instance of a recurring series, show the recurring delete dialog
        if (charge.isRecurringSeries && !charge.isParentRecurringCharge) {
          const deleteOption = await deleteRecurringRef.value?.open();
          if (!deleteOption) return; // User cancelled

          const mode = deleteOption === 'future' ? 'ThisAndFutureInstances' : 'ThisInstance';
          state.isDeleting = true;
          await chargesStore.deleteRecurring(charge.id, mode, true);
          handleSuccess(deleteOption === 'future' ? 'Recurring fees deleted successfully' : 'Fixed fee deleted successfully');
        } else {
          const confirmed = await deleteConfirmationRef.value?.open();
          if (!confirmed) return;
          state.isDeleting = true;
          await chargesStore.deleteCharge(charge.id);
          handleSuccess(`${fixedFeeTemplate ? 'Fixed fee template' : 'Fixed fee'} deleted successfully`);
        }
        cancel();
      } catch (error) {
        handleError(error);
      } finally {
        state.isDeleting = false;
      }
    };
    const cancel = fixedFee => {
      dialogOpen.value = false;
      if (props.isfromVue) {
        emit('fixedFeeCreated', fixedFee);
        return;
      }

      // Use original matter/client IDs if available (to go back to the original matter page)
      // This ensures that if matter was changed during editing, we still redirect to the original matter
      const clientId = state.originalClientId || formRef.value?.getChargeData()?.matterDetail?.client?.id;
      const matterId = state.originalMatterId || formRef.value?.getChargeData()?.matterDetail?.id;

      // Routes that open this modal from somewhere other than the matter's fixed fee
      // page (e.g. the invoice page) set stayOnRoute so the user comes back to where
      // they were instead of being sent to the matter
      if (!props.stateParams?.stayOnRoute && clientId && matterId) {
        return routeService.go('matters-next.client.matter.fixed', {
          id: clientId,
          matterId: matterId
        }, {
          reload: true
        });
      }
      routeService.go('^', {}, {
        reload: true
      });
    };
    const closeDialog = async () => {
      // Check for unsaved changes before closing
      if (formRef.value?.confirmDiscardChanges) {
        const proceed = await formRef.value.confirmDiscardChanges('closing the dialog');
        if (!proceed) {
          return; // User chose to keep editing
        }
      }
      if (props.isfromVue) {
        emit('close');
        return;
      }
      dialogOpen.value = false;
      routeService.go('^');
    };
    const createCharge = async chargeData => {
      return chargesStore.createChargeWithType(chargeData, fixedFeeTemplate);
    };
    const updateCharge = async (id, chargeData) => {
      const originalAmount = formRef.value.getOriginalAmount();
      await updateChargeWithMatterChange(id, chargeData, originalAmount);
    };
    const activate = async searchText => {
      if (formRef.value) {
        await formRef.value.activate(searchText);
      }
    };
    onMounted(() => {
      // Auto-open if not controlled by parent
      if (props.modelValue === undefined) {
        dialogOpen.value = true;
      }
      if (cardRef.value) {
        const el = cardRef.value.$el || cardRef.value;
        el?.focus?.();
      }
    });
    __expose({
      activate,
      formRef,
      saveCharge,
      closeDialog,
      handleChargeLoaded
    });
    return (_ctx, _cache) => {
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_card_title = _resolveComponent("v-card-title");
      const _component_v_card_text = _resolveComponent("v-card-text");
      const _component_v_spacer = _resolveComponent("v-spacer");
      const _component_v_card_actions = _resolveComponent("v-card-actions");
      const _component_v_card = _resolveComponent("v-card");
      const _component_v_dialog = _resolveComponent("v-dialog");
      return _openBlock(), _createBlock(_component_v_dialog, {
        modelValue: dialogOpen.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => dialogOpen.value = $event),
        "max-width": "800px",
        "content-class": "charge-edit-dialog",
        persistent: ""
      }, {
        default: _withCtx(() => [_createVNode(_component_v_card, {
          tabindex: "0",
          onKeydown: _withKeys(closeDialog, ["esc"]),
          ref_key: "cardRef",
          ref: cardRef,
          class: "focus:outline-none"
        }, {
          default: _withCtx(() => [_createVNode(_component_v_card_title, {
            class: "text-h5 px-6 pb-2"
          }, {
            default: _withCtx(() => [_createElementVNode("div", _hoisted_1, [_createElementVNode("span", null, _toDisplayString(chargeTitle.value), 1), _createVNode(LlSecondaryButton, {
              icon: "",
              variant: "text",
              size: "small",
              onClick: closeDialog
            }, {
              default: _withCtx(() => [_createVNode(_component_v_icon, null, {
                default: _withCtx(() => [...(_cache[1] || (_cache[1] = [_createTextVNode("mdi-close", -1)]))]),
                _: 1
              })]),
              _: 1
            })])]),
            _: 1
          }), _createVNode(_component_v_card_text, {
            class: "pt-2 px-6"
          }, {
            default: _withCtx(() => [_createVNode(CreateChargeForm, {
              ref_key: "formRef",
              ref: formRef,
              state: state,
              stateParams: __props.stateParams,
              rootScope: __props.rootScope,
              tracking: __props.tracking,
              isfromVue: __props.isfromVue,
              isPanel: false,
              entryPoint: effectiveEntryPoint.value,
              onChargeLoaded: handleChargeLoaded,
              onSave: saveCharge,
              onDelete: deleteCharge,
              onClose: closeDialog,
              onFixedFeeSaved: handleFixedFeeSaved,
              onSaveAndContinue: handleSaveAndContinue
            }, null, 8, ["state", "stateParams", "rootScope", "tracking", "isfromVue", "entryPoint"])]),
            _: 1
          }), _createVNode(_component_v_card_actions, {
            class: "px-6 pb-4"
          }, {
            default: _withCtx(() => [_createVNode(LlSecondaryButton, {
              onClick: closeDialog,
              disabled: state.isSaving || state.isDeleting,
              "data-cy": "cbff-cancel"
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [_createTextVNode(" Cancel ", -1)]))]),
              _: 1
            }, 8, ["disabled"]), state.chargeId && !state.isBilled ? (_openBlock(), _createBlock(LlSecondaryButton, {
              key: 0,
              variant: "outlined",
              color: "error",
              onClick: deleteCharge,
              loading: state.isDeleting,
              disabled: state.isSaving,
              "data-cy": "cbff-delete"
            }, {
              default: _withCtx(() => [_createVNode(_component_v_icon, {
                class: "mr-1"
              }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [_createTextVNode("mdi-delete", -1)]))]),
                _: 1
              }), _cache[4] || (_cache[4] = _createTextVNode(" Delete ", -1))]),
              _: 1
            }, 8, ["loading", "disabled"])) : _createCommentVNode("", true), _createVNode(_component_v_spacer), _createVNode(LlSecondaryButton, {
              onClick: saveCharge,
              loading: state.isSaving,
              disabled: state.isDeleting,
              color: "primary",
              variant: "flat",
              "data-cy": state.chargeId ? 'cbff-save' : 'cbff-create'
            }, {
              default: _withCtx(() => [_createTextVNode(_toDisplayString(state.chargeId ? 'Save' : 'Create') + " Fixed Fee" + _toDisplayString(_unref(fixedFeeTemplate) ? ' Template' : ''), 1)]),
              _: 1
            }, 8, ["loading", "disabled", "data-cy"])]),
            _: 1
          })]),
          _: 1
        }, 512), _createVNode(LlConfirmationDialog, {
          ref_key: "matterChangeConfirmationRef",
          ref: matterChangeConfirmationRef,
          title: "Confirm Matter Change",
          message: matterChangeMessage.value,
          confirmText: "Yes, Save",
          toggleText: "Cancel",
          showWarningIcon: true,
          "data-cy": "matter-change-confirmation"
        }, null, 8, ["message"]), _createVNode(LlConfirmationDialog, {
          ref_key: "deleteConfirmationRef",
          ref: deleteConfirmationRef,
          title: "Delete Fixed Fee",
          message: "Are you sure you want to delete this fixed fee?",
          confirmText: "Delete",
          toggleText: "Cancel",
          showWarningIcon: true,
          "data-cy": "delete-confirmation"
        }, null, 512), _createVNode(DeleteRecurringFeesDialog, {
          ref_key: "deleteRecurringRef",
          ref: deleteRecurringRef
        }, null, 512)]),
        _: 1
      }, 8, ["modelValue"]);
    };
  }
};